import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { compile } from 'path-to-regexp';
import * as R from 'ramda';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { Experience, SkillType } from '../../API';
import useTechSkills from '../../hooks/useTechSkills';
import TechnicalSkill from '../../models/technical-skills';
import routes from '../../routes';
import {
  ExperienceField,
  FormButtonsContainer,
  SubmitButton,
  ToggableChipsField,
} from '../Fields';
import LoadingPage from '../Loading/Page';
import useBasicProfileStorage from '../profile/basic/useBasicProfileStorage';
import SignInButton from '../SignInButton';
import {
  TypistAfterFinish, TypistDelay, TypistDelayLength, TypistItem, TypistWrapper,
} from '../Typist';

type SlideProps = {
  onNext: () => void;
};

const useStyles = makeStyles((theme) => ({
  nextSlideButton: {
    marginTop: theme.spacing(2),
    display: 'block',
  },
  slideTextBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    margin: '0 5vw',
    '& > div': {
      borderLeft: '5px solid black',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingLeft: 20,
    },
    '& > div > p': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    '& .first': {
      fontSize: 'xx-large',
      marginBottom: theme.spacing(1),
    },
    '& .second': {
      fontSize: 'large',
      marginBottom: theme.spacing(1),
    },
    '& .third': {
      fontSize: 'x-large',
      marginBottom: theme.spacing(1),
    },
  },
  profileSlide: {
    flexDirection: 'column-reverse',
    flex: 1,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    '& > *': {
      flex: 'initial',
      margin: theme.spacing(2),
    },
    '@media (min-width: 760px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& > *': {
        flex: 1,
      },
    },
  },
  profileBuilder: {
    marginTop: theme.spacing(2),
  },
  form: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  signupButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    '& > *': {
      marginTop: theme.spacing(2),
    },
  },
}));

function NextSlideButton({ text, onNext }: { text: string, onNext: () => void }) {
  const classes = useStyles();
  return (
    <Button variant="contained" color="primary" autoFocus onClick={onNext} className={classes.nextSlideButton}>
      {text}
    </Button>
  );
}

const Slide1Schema = Yup.object().shape({
  firstName: Yup.string()
    .required('We really want to get to know you')
    .lowercase()
    .min(2, 'I bet you have more characters in your name, right?'),
});

export function Slide1({ onNext }: SlideProps) {
  const classes = useStyles();
  const { basicProfile, writeBasicProfile, fetchingBasicProfile } = useBasicProfileStorage();
  const [inputFocused, setInputFocused] = useState(false);
  if (fetchingBasicProfile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper>
          <TypistItem className="first">
            Hello there!
            <TypistDelay length={TypistDelayLength.normal} />
            {' '}
            We are Matilda Explorations
            <TypistDelay length={TypistDelayLength.long} />
          </TypistItem>
          <TypistItem className="second">
            Our job is to help great Latin American engineers find full-time,
            remote positions at the best Silicon Valley companies
            <TypistDelay length={TypistDelayLength.long} />
          </TypistItem>
          <TypistItem className="third">
            What&apos;s your first name?
            <TypistDelay length={TypistDelayLength.short} />
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={basicProfile}
              validationSchema={Slide1Schema}
              onSubmit={(values) => {
                writeBasicProfile({
                  ...values,
                  firstName: `${R.head(values.firstName).toUpperCase()}${R.tail(values.firstName)}`,
                });
                onNext();
              }}
            >
              <Form className={classes.form}>
                <Field
                  component={TextField}
                  autoFocus
                  autoComplete="given-name"
                  name="firstName"
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="submit" type="submit">
                          <KeyboardReturnIcon color={inputFocused ? 'primary' : 'inherit'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Form>
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

export function Slide2({ onNext }: SlideProps) {
  const classes = useStyles();
  const { basicProfile, fetchingBasicProfile } = useBasicProfileStorage();
  if (fetchingBasicProfile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="first">
            Nice to meet you,
            {' '}
            {basicProfile?.firstName}
            !
          </TypistItem>
          <TypistItem className="second">
            Just complete these couple of questions with your GitHub account and we&apos;ll show
            you the open positions that might be interesting for you.
          </TypistItem>
          <TypistAfterFinish>
            <NextSlideButton text="Sounds good" onNext={onNext} />
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

const Slide3Schema = Yup.object().shape({
  experience: Yup.string()
    .required('Please fill your experience range')
    .oneOf(R.keys(Experience)),
});

export function Slide3({ onNext }: SlideProps) {
  const classes = useStyles();
  const { basicProfile, fetchingBasicProfile, writeBasicProfile } = useBasicProfileStorage();
  if (fetchingBasicProfile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second">
            What is your experience in terms of years
            working professionally in Software Engineering?
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={basicProfile}
              validationSchema={Slide3Schema}
              onSubmit={(values) => {
                writeBasicProfile(values);
                onNext();
              }}
            >
              <Form className={classes.form}>
                <ExperienceField name="experience" autoFocus />
                <FormButtonsContainer>
                  <SubmitButton text="Continue" />
                </FormButtonsContainer>
              </Form>
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

export const toWelcome4Path = compile(routes.welcome.withPage);

const skillsSchema = Yup.object().shape({
  skills: Yup.array().min(1, 'Please select at least one skill'),
});

export function Slide4({ onNext }: SlideProps) {
  const classes = useStyles();
  const { basicProfile, writeBasicProfile, fetchingBasicProfile } = useBasicProfileStorage();
  const { skills: openJobsSkills, fetchingSkills: fetchingOpenJobsSkills } = useTechSkills({
    type: SkillType.MAIN,
    withOpenJobs: true,
    experienceForOpenJobs: basicProfile.experience,
  });
  const { skills, fetchingSkills } = useTechSkills({
    type: SkillType.MAIN,
    withOpenJobs: false,
  });
  const loadingSkills = fetchingOpenJobsSkills || fetchingSkills;
  const loading = fetchingBasicProfile || loadingSkills;
  const [wantsExpandedSkills, setWantsExpandedSkills] = useState<boolean>();
  if (loading) return <LoadingPage />;
  const selectedSkills = basicProfile.skills;
  const selectedSkillsAvailable = R.intersection(selectedSkills, skills || []);
  const selectedSkillsWithOpenJobs = R.intersection(selectedSkills, openJobsSkills || []);
  const hasOpenJobSkills = Boolean(openJobsSkills?.length);
  const showExpandedSkills = wantsExpandedSkills
    || !hasOpenJobSkills
    || (
      wantsExpandedSkills !== false
        && !R.equals(selectedSkillsWithOpenJobs, selectedSkillsAvailable));
  const initialValues = {
    ...basicProfile,
    skills: showExpandedSkills ? selectedSkillsAvailable : selectedSkillsWithOpenJobs,
  };
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second" key="noneOfSkills">
            {hasOpenJobSkills
              ? `Considering your years of experience, at the moment we have open jobs
                in the following technologies. In which of these technologies you feel strong
                enough to face selection processes and to be productive from day 1?`
              : `It looks like we don't currently have open jobs for your experience level. But if you want
                you can still create your account, selecting from all the main skills we have.
                That way we can send you a notification when there is a new job matching
                your experience and skills. Select all the skills you feel strong enough
                to face selection processes and to be productive from day 1.`}
          </TypistItem>
          <TypistAfterFinish>
            {hasOpenJobSkills && (
              <Typography color="textSecondary" align="center">
                Expand the list if you want to be notified of new jobs in other technologies
              </Typography>
            )}
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={skillsSchema}
              onSubmit={(values) => {
                writeBasicProfile(values);
                onNext();
              }}
            >
              {({ errors }) => (
                <Form className={classes.form}>
                  {hasOpenJobSkills && (
                    <>
                      <ToggableChipsField
                        autoFocus
                        name="skills"
                        options={openJobsSkills || []}
                        getOptionLabel={(skill) => skill.name}
                      />
                      <Box justifyContent="center" display="flex">
                        <Button
                          onClick={() => setWantsExpandedSkills(!showExpandedSkills)}
                          startIcon={showExpandedSkills ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                        >
                          {showExpandedSkills ? 'With open jobs only' : 'Expand technologies'}
                        </Button>
                      </Box>
                    </>
                  )}
                  {showExpandedSkills && (
                    <ToggableChipsField
                      name="skills"
                      options={R.difference<TechnicalSkill>(skills || [], openJobsSkills || [])}
                      getOptionLabel={(skill) => skill.name}
                    />
                  )}
                  <FormButtonsContainer>
                    {errors.skills
                      ? <Typography color="secondary">{errors.skills}</Typography>
                      : null}
                    <SubmitButton text="Continue" />
                  </FormButtonsContainer>
                </Form>
              )}
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

export function Slide5() {
  const classes = useStyles();
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal}>
          <TypistItem className="second">
            Save your profile with your GitHub account and you&apos;ll see all the details about
            the currently open positions we might have for you.
          </TypistItem>
          <TypistAfterFinish>
            <Box className={classes.signupButton}>
              <SignInButton text="Sign up" returnTo={routes.welcome.finishSignup} autoFocus size="large" />
              <Typography variant="caption" color="textSecondary">
                We&apos;ll only get your name, email, username, and avatar from GitHub
              </Typography>
            </Box>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}
